import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Loader, Modal, Table } from '@itds/elements/index.js';
import { ZeroState } from '@itds/components/index.js';
import { QUOTE_ACTIONS } from '@itds/Constants';

import Toolbar from '@brokerage/components/Toolbar';
import EditQuoteActionModal from './EditQuoteActionModal';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

import { Api } from '@brokerage/utils/Api';

const QuoteActions = ({ annuity_quoter, setErrorMessage, setSuccessMessage }) => {
  const { agency } = useContext(AgencyContext);

  const [loading, setLoading] = useState(true);
  const [quote_actions, setQuoteActions] = useState([]);
  const [modal, setModal] = useState(null);
  const [action, setAction] = useState(null);

  useEffect(() => {
    loadCustomActions();
  }, []);

  const loadCustomActions = () => {
    setLoading(true);

    Api.loadAnnuityQuoterCustomActions(annuity_quoter.token).then(response => {
      setQuoteActions(response.data);
      setLoading(false);
    });
  };

  const showAction = action => {
    setAction(action);
    setModal('edit');
  };

  const handleSaveClick = action => {
    action.annuity_quoter_token = annuity_quoter.token;

    Api.saveAnnuityQuoterCustomAction(action.id, action)
      .then(() => {
        setModal(null);
        loadCustomActions();
        setSuccessMessage('Quote Action updated');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setModal(null);
        setErrorMessage('Unable to update the Quote Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const handleDelete = (action, e) => {
    setAction(action);
    setModal('delete');

    e.stopPropagation();
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);

    Api.deleteAnnuityQuoterCustomAction(action.id)
      .then(() => {
        setModal(null);
        setAction(null);
        loadCustomActions();
        setSuccessMessage('Quote Action deleted');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setAction(null);
        setModal(null);
        setErrorMessage('Unable to delete the Quote Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const action_types = [
    {
      label: 'Basic',
      options: QUOTE_ACTIONS.filter(item => item.group === 'Basic')
    },
    {
      label: 'Defaults',
      options: QUOTE_ACTIONS.filter(item => item.group === 'Default')
    },
    {
      label: 'Integrations',
      options: QUOTE_ACTIONS.filter(item => item.group === 'Integration')
    }
  ];

  return loading ? (
    <Loader />
  ) : (
    <>
      {modal === 'edit' ? (
        <EditQuoteActionModal
          actionTypes={action_types}
          agency={agency}
          availableCarrierIds={annuity_quoter.carrier_ids || []}
          availableProductIds={annuity_quoter.product_ids || []}
          beta={false} // TODO: pull this from quoter object
          onClose={() => {
            setModal(null);
            setAction(null);
          }}
          onSave={handleSaveClick}
          quoteAction={action}
          showFaceAmounts={true} // Don't know if we need this
          showOpenInModal={true} // Don't know if we need this
        />
      ) : null}

      {modal === 'delete' ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
            setAction(null);
          }}
          title={`Delete "${action.label}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete the "{action.label}" quote action?
          </div>
        </Modal>
      ) : null}

      <Toolbar
        buttons={[
          {
            label: 'New Quote Action',
            onClick: () => {
              setAction(null);
              setModal('edit');
            }
          }
        ]}
      />

      {quote_actions.length ? (
        <Table
          columns={[
            {
              id: 'label',
              label: 'label',
              render: action => {
                return <Link to={''}>{action.label ? action.label : 'N/A'}</Link>;
              }
            },
            {
              id: 'action_type',
              label: 'type',
              render: action => {
                const quote_action = QUOTE_ACTIONS.find(qa => qa.value === action.action_type);

                return <>{quote_action ? quote_action.label : 'N/A'}</>;
              }
            },
            {
              id: 'order',
              label: 'order'
            },
            {
              id: 'carriers',
              label: 'carriers',
              render: action => {
                return <>{action.carrier_ids && action.carrier_ids.length} Carriers</>;
              }
            },
            {
              id: '',
              render: action => {
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button color='RED' onClick={handleDelete.bind(null, action)} small={true}>
                      Delete
                    </Button>
                  </div>
                );
              }
            }
          ]}
          data={quote_actions}
          onRowClick={showAction}
        />
      ) : (
        <ZeroState icon='text-search' title="This quoter doesn't have any Quote Actions" />
      )}
    </>
  );
};

QuoteActions.propTypes = {
  annuity_quoter: PropTypes.object,
  setErrorMessage: PropTypes.func,
  setSuccessMessage: PropTypes.func
};

export default QuoteActions;
